import { useState, useCallback, useEffect, useMemo, CSSProperties } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Divider from '@mui/material/Divider';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Video from 'yet-another-react-lightbox/plugins/video';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import { DataInputObjectUpload } from './UploadObject';
import { useDeleteDataInputObject, useGetDisplayObjects } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { DataInputObjectFile } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { DATA_INPUT_OBJECT_TYPE } from 'constant/DataInputConstant';
import { ModalPDFViewer } from './ModalPDFViewer';
import { ModalVideoViewer } from './ModalVideoViewer';
import { getYoutubeThumbnailFromURL } from 'utils/String';
import { Box, Chip, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ModalDeleteDataInputObjectConfirmation from './ModalDeleteConfirmation';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/captions.css';

// ─── Constants & Config ──────────────────────────────────────────────────────

const TAB_ALL = 'ALL';

const SECTION_CONFIG = [
  { key: 'IMAGES_ATTACHMENT', label: 'Images', objectType: DATA_INPUT_OBJECT_TYPE.IMAGE, icon: ImageOutlinedIcon, color: '#6C63FF', bg: '#EDE9FF' },
  { key: 'DOCUMENTS_ATTACHMENT', label: 'Documents', objectType: DATA_INPUT_OBJECT_TYPE.DOCUMENT, icon: InsertDriveFileOutlinedIcon, color: '#E67E22', bg: '#FEF3E2' },
  { key: 'VIDEO_CONTENT', label: 'Videos', objectType: DATA_INPUT_OBJECT_TYPE.VIDEO, icon: VideoLibraryOutlinedIcon, color: '#E74C3C', bg: '#FDEDEC' },
  { key: 'EXTERNAL_LINKS', label: 'Links', objectType: DATA_INPUT_OBJECT_TYPE.LINK, icon: LinkOutlinedIcon, color: '#2980B9', bg: '#E8F4FD' },
] as const;

const MAP_DOC_TYPE_WITH_ATTACHMENT_TYPE = new Map<string, string>([
  ['IMAGE', 'IMAGES_ATTACHMENT'],
  ['DOCUMENT', 'DOCUMENTS_ATTACHMENT'],
  ['VIDEO', 'VIDEO_CONTENT'],
  ['LINK', 'EXTERNAL_LINKS'],
]);

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Styles = {
  modalStyle: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '85%',
    maxWidth: 1100,
    bgcolor: 'background.paper',
    borderRadius: 2.5,
    px: 3,
    py: 2.5,
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
  },
  scrollArea: {
    flex: 1,
    overflow: 'auto',
    px: 0.5,
    mt: 1.5,
    '&::-webkit-scrollbar': { width: 6 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#C4C4C4', borderRadius: 3 },
  },
  tabBar: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap' as const,
    py: 1.5,
  },
  itemCard: {
    borderRadius: 2,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
    },
    '&:hover .overlay': {
      opacity: 1,
    },
  },
  boxOverlay: {
    opacity: 0,
    transition: 'opacity 0.25s ease',
    backdropFilter: 'blur(2px)',
  },
  imgStyle: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    display: 'block',
  } as CSSProperties,
  videoStyle: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    display: 'block',
  } as CSSProperties,
  contentBoxItem: {
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    bgcolor: '#F8F9FB',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
  },
  sectionDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1.5,
    mt: 0.5,
  },
  emptyState: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    py: 4,
    color: '#A0AEC0',
    borderRadius: 2,
    border: '1px dashed #E2E8F0',
    bgcolor: '#FAFBFC',
  },
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModalPreviewAttachmentProps {
  visible?: boolean;
  onClose: () => void;
  onChange: (fieldName: string, value: string | null) => void;
  fileUrl: string;
  subjectId: string;
  fieldName: string;
  fieldLabel: string;
  initialObjectIds?: string[];
  viewOnly?: boolean;
}

interface PreviewAttachmentData {
  [key: string]: DataInputObjectFile[];
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function ModalPreviewAttachment(props: ModalPreviewAttachmentProps) {
  const {
    onClose,
    visible = false,
    subjectId,
    fieldName,
    onChange,
    initialObjectIds,
    fieldLabel,
    viewOnly = true,
  } = props;

  // ── State ──────────────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<string>(TAB_ALL);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [selectedItemToDelete, setSelectedItemToDelete] = useState<{
    data: DataInputObjectFile;
    sectionIndex: number;
  } | null>(null);

  const [selectedPDFViewer, setSelectedPDFViewer] = useState<{
    open: boolean;
    title: string;
    pdfURL: string;
  }>({ open: false, title: '', pdfURL: '' });

  const [selectedVideoViewer, setSelectedVideoViewer] = useState<{
    open: boolean;
    title: string;
    videoURL: string;
  }>({ open: false, title: '', videoURL: '' });

  const [objectIds, setObjectIds] = useState<string[]>([]);

  const [previewData, setPreviewData] = useState<PreviewAttachmentData>({
    IMAGES_ATTACHMENT: [],
    DOCUMENTS_ATTACHMENT: [],
    VIDEO_CONTENT: [],
    EXTERNAL_LINKS: [],
  });

  const [objectIdsToDelete, setObjectIdsToDelete] = useState<string[]>([]);
  const [newlyUploadedIds, setNewlyUploadedIds] = useState<string[]>([]);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: objectsData, isLoading: isLoadingObjectData } = useGetDisplayObjects(objectIds, subjectId);
  const { mutateAsync: deleteDataInputObject, isLoading: isDeleting } = useDeleteDataInputObject();

  // ── Derived ────────────────────────────────────────────────────────────────

  const totalAttachments = useMemo(() => {
    return (
      previewData.IMAGES_ATTACHMENT.length +
      previewData.DOCUMENTS_ATTACHMENT.length +
      previewData.VIDEO_CONTENT.length +
      previewData.EXTERNAL_LINKS.length
    );
  }, [previewData]);

  const lightboxSlides = useMemo(() => {
    return (previewData.IMAGES_ATTACHMENT || []).map((item) => ({
      src: item.link,
      title: item.objectName,
      description: item.description,
    }));
  }, [previewData.IMAGES_ATTACHMENT]);

  const activeSection = useMemo(() => {
    return SECTION_CONFIG.find((s) => s.objectType === activeTab) || null;
  }, [activeTab]);

  const sectionsToRender = useMemo(() => {
    if (activeTab === TAB_ALL) return SECTION_CONFIG;
    const found = SECTION_CONFIG.find((s) => s.objectType === activeTab);
    return found ? [found] : SECTION_CONFIG;
  }, [activeTab]);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (initialObjectIds) {
      setObjectIds(initialObjectIds);
    }
  }, [initialObjectIds]);

  useEffect(() => {
    if (objectsData?.data) {
      setPreviewData({
        IMAGES_ATTACHMENT: objectsData.data.filter((item) => item.objectType === 'IMAGE'),
        DOCUMENTS_ATTACHMENT: objectsData.data.filter((item) => item.objectType === 'DOCUMENT'),
        VIDEO_CONTENT: objectsData.data.filter((item) => item.objectType === 'VIDEO'),
        EXTERNAL_LINKS: objectsData.data.filter((item) => item.objectType === 'LINK'),
      });
    }
  }, [objectsData?.data]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleImageClick = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const resetModalState = () => {
    setObjectIds([]);
    setObjectIdsToDelete([]);
    setNewlyUploadedIds([]);
    setActiveTab(TAB_ALL);
    setPreviewData({
      IMAGES_ATTACHMENT: [],
      DOCUMENTS_ATTACHMENT: [],
      VIDEO_CONTENT: [],
      EXTERNAL_LINKS: [],
    });
  };

  const handleCloseModal = async () => {
    // Cancel/close without saving — clean up newly uploaded files from backend
    if (newlyUploadedIds.length > 0) {
      try {
        await deleteDataInputObject({ ids: newlyUploadedIds });
      } catch {}
    }
    onClose();
    resetModalState();
  };

  const handleOnSaveClick = async () => {
    try {
      // Delete removed items
      if (objectIdsToDelete.length > 0) {
        await deleteDataInputObject({ ids: objectIdsToDelete });
      }

      // Compute final IDs: initial - deleted + newly uploaded
      const baseIds = initialObjectIds || [];
      const finalIds = [
        ...baseIds.filter((id) => !objectIdsToDelete.includes(id)),
        ...newlyUploadedIds,
      ];
      onChange(fieldName, finalIds.length > 0 ? finalIds.join(',') : null);

      onClose();
      resetModalState();
    } catch {}
  };

  const handleOnUploadCompleted = (newObjectIds: string[]) => {
    const updateObjectIds = [...objectIds, ...newObjectIds];
    setObjectIds(updateObjectIds);
    setNewlyUploadedIds((prev) => [...prev, ...newObjectIds]);
    // Don't call onChange here — deferred to Save
  };

  const handleOnDeleteObjectItem = (itemObj: DataInputObjectFile, sectionIndex: number) => {
    const docType = MAP_DOC_TYPE_WITH_ATTACHMENT_TYPE.get(itemObj.objectType);
    if (docType) {
      if (Boolean(itemObj.id)) {
        setObjectIdsToDelete((prev) => [...prev, itemObj.id]);
      }
      const updated = [...previewData[docType]];
      updated.splice(sectionIndex, 1);
      setPreviewData((old) => ({ ...old, [docType]: updated }));
    }
    setSelectedItemToDelete(null);
  };

  const handleViewItem = (item: DataInputObjectFile, index: number) => {
    switch (item.objectType) {
      case 'IMAGE':
        handleImageClick(index);
        break;
      case 'DOCUMENT':
        setSelectedPDFViewer({ open: true, title: item.objectName, pdfURL: item.link });
        break;
      case 'VIDEO':
        setSelectedVideoViewer({ open: true, title: item.objectName, videoURL: item.link });
        break;
      case 'LINK':
        window.open(item.link, '_blank', 'noopener,noreferrer');
        break;
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderItemContent = (item: DataInputObjectFile) => {
    switch (item.objectType) {
      case 'IMAGE':
        return <img src={item.link} alt={item.objectName} style={Styles.imgStyle} />;
      case 'VIDEO':
        return (
          <Box position='relative' width='100%' height='100%'>
            <video
              src={`${item.link}#t=0.1`}
              poster={getYoutubeThumbnailFromURL(item.link)}
              preload='metadata'
              style={Styles.videoStyle}
            />
            <Box position='absolute' top='50%' left='50%' sx={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
              <PlayCircleOutlineIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.85)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }} />
            </Box>
          </Box>
        );
      case 'DOCUMENT': {
        const ext = getFileExtension(item.objectName);
        return (
          <Stack alignItems='center' sx={Styles.contentBoxItem} justifyContent='center' p={1.5} spacing={0.5}>
            <DescriptionIcon sx={{ fontSize: 32, color: '#E67E22' }} />
            {ext && <Chip label={ext} size='small' sx={{ fontSize: 10, height: 18, bgcolor: '#FEF3E2', color: '#E67E22', fontWeight: 600 }} />}
            <Tooltip title={item.objectName}>
              <Typography variant='input-label-gray' component='p' fontSize='11px' textAlign='center' textOverflow='ellipsis' overflow='hidden' whiteSpace='nowrap' width='100%' mt={0.5}>
                {item.objectName}
              </Typography>
            </Tooltip>
          </Stack>
        );
      }
      case 'LINK':
        return (
          <Stack alignItems='center' sx={Styles.contentBoxItem} justifyContent='center' p={1.5} spacing={0.5}>
            <LaunchOutlinedIcon sx={{ fontSize: 32, color: '#2980B9' }} />
            <Tooltip title={item.objectName}>
              <Typography variant='input-label-gray' component='p' fontSize='11px' textAlign='center' textOverflow='ellipsis' overflow='hidden' whiteSpace='nowrap' width='100%' mt={0.5}>
                {item.objectName}
              </Typography>
            </Tooltip>
          </Stack>
        );
      default:
        return null;
    }
  };

  const renderSection = (section: (typeof SECTION_CONFIG)[number], showHeader: boolean) => {
    const SectionIcon = section.icon;
    const items = previewData[section.key] || [];

    return (
      <Stack key={section.key}>
        {showHeader && (
          <Box sx={Styles.sectionDivider}>
            <Box sx={{ width: 24, height: 24, borderRadius: 0.75, bgcolor: section.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <SectionIcon sx={{ fontSize: 14, color: section.color }} />
            </Box>
            <Typography variant='input-label-gray' component='h2' fontWeight='bold' fontSize={13}>
              {section.label}
            </Typography>
            <Chip
              label={items.length}
              size='small'
              sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: items.length > 0 ? section.bg : '#F1F3F5', color: items.length > 0 ? section.color : '#8895A7' }}
            />
          </Box>
        )}
        <Box sx={Styles.gridContainer}>
          {items.map((item, index) => (
            <Box key={item.id || item.objectName} position='relative' sx={{ ...Styles.itemCard, aspectRatio: '1 / 1' }}>
              {renderItemContent(item)}
              <ContentOverlayAction
                viewOnly={viewOnly}
                objectName={item.objectName}
                onDeleteClick={() => setSelectedItemToDelete({ data: item, sectionIndex: index })}
                onViewClick={() => handleViewItem(item, index)}
              />
            </Box>
          ))}
          {items.length === 0 && (
            <Box sx={Styles.emptyState}>
              <SectionIcon sx={{ fontSize: 28, color: '#CBD5E0', mb: 0.5 }} />
              <Typography variant='caption' sx={{ color: '#A0AEC0' }}>
                No {section.label.toLowerCase()} attached
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    );
  };

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <>
      <div>
        <Modal open={visible} sx={{ zIndex: 1000 }}>
          <Stack sx={Styles.modalStyle}>
            {/* ── Header ─────────────────────────────────────── */}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Stack>
                <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                  {fieldLabel}
                </Typography>
                {!isLoadingObjectData && (
                  <Typography variant='caption' sx={{ color: '#8895A7', mt: -0.3 }}>
                    {totalAttachments} {totalAttachments === 1 ? 'attachment' : 'attachments'}
                  </Typography>
                )}
              </Stack>
              <IconButton onClick={handleCloseModal} size='small'>
                <HighlightOffRoundedIcon sx={{ color: '#98A2AE', fontSize: 22 }} />
              </IconButton>
            </Stack>

            {/* ── Tab Bar ────────────────────────────────────── */}
            {!isLoadingObjectData && (
              <Box sx={Styles.tabBar}>
                <FilterTab
                  label='All'
                  count={totalAttachments}
                  icon={<AppsOutlinedIcon sx={{ fontSize: 15 }} />}
                  active={activeTab === TAB_ALL}
                  activeColor='#3B4797'
                  activeBg='#E8EAF6'
                  onClick={() => setActiveTab(TAB_ALL)}
                />
                {SECTION_CONFIG.map((section) => {
                  const SIcon = section.icon;
                  return (
                    <FilterTab
                      key={section.key}
                      label={section.label}
                      count={(previewData[section.key] || []).length}
                      icon={<SIcon sx={{ fontSize: 15 }} />}
                      active={activeTab === section.objectType}
                      activeColor={section.color}
                      activeBg={section.bg}
                      onClick={() => setActiveTab(section.objectType)}
                    />
                  );
                })}
              </Box>
            )}

            <Divider />

            {/* ── Body ───────────────────────────────────────── */}
            {isLoadingObjectData && (
              <Stack flex={1} justifyContent='center' alignItems='center' minHeight={300}>
                <ActivityIndicator />
              </Stack>
            )}

            {!isLoadingObjectData && (
              <Stack sx={Styles.scrollArea}>
                {/* Inline upload — shown in edit mode when a specific type tab is active */}
                {!viewOnly && activeTab !== TAB_ALL && activeSection && (
                  <Box sx={{ mb: 2 }}>
                    <DataInputObjectUpload
                      name={fieldName}
                      objectType={activeSection.objectType}
                      onUploadComplete={handleOnUploadCompleted}
                      subjectId={subjectId}
                      label={activeSection.label}
                    />
                  </Box>
                )}

                {/* Grid content */}
                <Stack spacing={activeTab === TAB_ALL ? 3 : 0}>
                  {sectionsToRender.map((section) => renderSection(section, activeTab === TAB_ALL))}
                </Stack>
              </Stack>
            )}

            {/* ── Footer ─────────────────────────────────────── */}
            {!viewOnly && !isLoadingObjectData && (
              <>
                <Divider sx={{ mt: 1 }} />
                <Stack direction='row' justifyContent='flex-end' gap={1.5} mt={1.5}>
                  <Button variant='outlined' onClick={handleCloseModal} sx={{ width: 100, textTransform: 'none' }}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={isDeleting}
                    sx={{ width: 120, textTransform: 'none' }}
                    variant='main-table-panel'
                    onClick={handleOnSaveClick}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </>
            )}
          </Stack>
        </Modal>
      </div>

      {/* ── Viewers ──────────────────────────────────────── */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
        plugins={[Thumbnails, Zoom, Video, Captions]}
      />
      <ModalPDFViewer
        open={selectedPDFViewer.open}
        pdfURL={selectedPDFViewer.pdfURL}
        title={selectedPDFViewer.title}
        onClose={() => setSelectedPDFViewer({ open: false, title: '', pdfURL: '' })}
      />
      <ModalVideoViewer
        open={selectedVideoViewer.open}
        videoURL={selectedVideoViewer.videoURL}
        title={selectedVideoViewer.title}
        onClose={() => setSelectedVideoViewer({ open: false, title: '', videoURL: '' })}
      />
      <ModalDeleteDataInputObjectConfirmation
        visible={Boolean(selectedItemToDelete)}
        item={selectedItemToDelete || undefined}
        onCancel={() => setSelectedItemToDelete(null)}
        onClose={() => setSelectedItemToDelete(null)}
        onApprove={handleOnDeleteObjectItem}
      />
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FilterTab({
  label,
  count,
  icon,
  active,
  activeColor,
  activeBg,
  onClick,
}: {
  label: string;
  count: number;
  icon: React.ReactNode;
  active: boolean;
  activeColor: string;
  activeBg: string;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      onClick={onClick}
      startIcon={icon}
      sx={{
        textTransform: 'none',
        borderRadius: 5,
        px: 1.5,
        py: 0.5,
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        color: active ? activeColor : '#667085',
        bgcolor: active ? activeBg : 'transparent',
        border: active ? `1px solid ${activeColor}30` : '1px solid transparent',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: active ? activeBg : '#F5F5F5',
        },
      }}
    >
      {label}
      <Chip
        label={count}
        size='small'
        sx={{
          ml: 0.75,
          height: 18,
          fontSize: 10,
          fontWeight: 700,
          bgcolor: active ? `${activeColor}20` : '#EAECF0',
          color: active ? activeColor : '#667085',
        }}
      />
    </Button>
  );
}

function ContentOverlayAction({
  onViewClick,
  onDeleteClick,
  viewOnly,
  objectName,
}: {
  onViewClick: () => void;
  onDeleteClick: () => void;
  viewOnly: boolean;
  objectName?: string;
}) {
  return (
    <Box
      className='overlay'
      position='absolute'
      top={0}
      left={0}
      width='100%'
      height='100%'
      display='flex'
      justifyContent='center'
      alignItems='center'
      bgcolor='rgba(59, 71, 151, 0.65)'
      sx={Styles.boxOverlay}
    >
      <Stack direction='row' spacing={0.5}>
        <Tooltip title={`View ${objectName}`} placement='top'>
          <IconButton
            size='small'
            onClick={onViewClick}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {!viewOnly && (
          <Tooltip title={`Delete ${objectName}`} placement='top'>
            <IconButton
              onClick={onDeleteClick}
              size='small'
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(231,76,60,0.7)' } }}
            >
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
